import { z } from 'zod'
import { AdSortBy, AdType, ListViewMode, SortDirection } from '@/api/dto'
import { DEFAULT_AD_SORT_BY, DEFAULT_AD_SORT_DIR } from '@/components/ad/adSortOptions'
import {
  COMMON_ALIAS,
  optionalNumberField,
  pageField,
  searchField,
  sizeField,
  tagIdsField,
} from './common'

export const AD_PAGE_SIZE = 12

export const adFiltersSchema = z.object({
  search: searchField,
  adType: z.enum(AdType).optional().catch(undefined),
  /** Either bound excludes the ads with no price ("à négocier") — see `ListAdsParams`. */
  minPrice: optionalNumberField,
  maxPrice: optionalNumberField,
  /** `?tags=<id>,<id>`, in OR (plan D6, D18). */
  tags: tagIdsField,
  sortBy: z.enum(AdSortBy).default(DEFAULT_AD_SORT_BY).catch(DEFAULT_AD_SORT_BY),
  sortDir: z.enum(SortDirection).default(DEFAULT_AD_SORT_DIR).catch(DEFAULT_AD_SORT_DIR),
  page: pageField,
  size: sizeField(AD_PAGE_SIZE),
})

export type AdFilters = z.infer<typeof adFiltersSchema>

export const adFiltersAlias = {
  ...COMMON_ALIAS,
  adType: 'type',
  minPrice: 'pmin',
  maxPrice: 'pmax',
  sortBy: 'sort',
  sortDir: 'dir',
} as const

/**
 * True as soon as a criterion *narrows* the result set. Sorting is not one: it cannot empty a
 * list, so it has no place in diagnosing a dead end — nor in what "clear filters" resets.
 */
export function isAdFiltered(filters: AdFilters): boolean {
  return (
    !!filters.search?.trim() ||
    !!filters.adType ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    !!filters.tags?.length
  )
}

/**
 * Projects an ad list's filters onto the endpoint's params. Shared with the `ads` route's
 * `prefetch`, which resolves the same filters out of the request's query string.
 */
export function adApiParams(filters: AdFilters) {
  return { ...filters, view: ListViewMode.COMPACT }
}
